import { segmentPhonemes } from "../phonemes/segmentPhonemes";

/**
 * run through the segments for all the languages to count them up
 * @returns a map from phoneme segment to the number of occurences across all languages
 */
export function countSegments(languageToSegments: Record<string, string>): Map<string, number> {
  const result: Map<string, number> = new Map<string, number>();
  const names = Object.getOwnPropertyNames(languageToSegments);
  for (const name of names) {
    const all = languageToSegments[name];
    const segments = all.split(' ');
    for (const s of segments) {
      if (result.has(s)) {
        result.set(s, result.get(s) as number + 1);
      } else {
        result.set(s, 1);
      }
    }
  }
  return result;
}

/**
 * run through the phonemes for all the languages to count them up,
 * tracking any matches from 'sets' as well
 * @returns a map from phoneme symbol(s) to the number of occurences across all languages
 */
export function countPhonemeSymbols(
  languageToSegments: Record<string, string>, sets: string[],
): Map<string, number> {
  const result: Map<string, number> = new Map<string, number>();
  const tracking: Set<string> = new Set<string>(sets);

  const names = Object.getOwnPropertyNames(languageToSegments);
  for (const name of names) {
    const all = languageToSegments[name];
    const unique = getUnique(all, tracking);
    unique.forEach(s => {
      if (result.has(s)) {
        result.set(s, result.get(s) as number + 1);
      } else {
        result.set(s, 1);
      }
    });

    sets.forEach(pair => {
      // note: if 'pair' is in 'tracking', we already counted it
      if (all.includes(pair) && !tracking.has(pair)) {
        if (result.has(pair)) {
          result.set(pair, result.get(pair) as number + 1);
        } else {
          result.set(pair, 1);
        }
      }
    });
  }

  return result;
}

// get the set of all unique phonemes or pieces of phonemes,
// if a segment is in 'tracking', don't track its individual pieces
function getUnique(segments: string, tracking: Set<string>): Set<string> {
  const unique = new Set<string>();
  const pieces = segments.split(' ');
  for (const piece of pieces) {
    if (tracking.has(piece)) {
      // full piece as-is
      unique.add(piece);
    } else {
      // decompose, but keep diacritics & affricates together
      const segments = segmentPhonemes(piece, 'ipa', 'all-aff');
      for (const segment of segments.phonemes) {
        const p = segment.phoneme;
        if (tracking.has(p)) {
          // track segment
          unique.add(p);
        } else {
          // we weren't asked to track the larger pieces, so track individual characters
          for (let i = 0; i < p.length; i++) {
            const c = p.charAt(i);
            unique.add(c);
          }
        }
      }
    }
  }
  return unique;
}
